import { appConfig } from "@/constants";
import { usePassportProvider } from "@/context/PassportProvider";
import { useAccessToken } from "@/hooks";
import { authorizedGet } from "@/utils/request";
import axios from "axios";
import {
  type PropsWithChildren,
  createContext,
  useContext,
  useEffect,
} from "react";
import useSWR from "swr";

export type LeaderboardEntry = {
  walletAddress: string;
  xUsername?: string;
  xUserId?: string;
  balance: number;
  rank: number;
};

export type LeaderboardWinCondition = {
  pointsFromWinning: number;
  thresholdRank: number;
};

export type LeaderboardData = {
  topEntries: LeaderboardEntry[];
  userEntry?: LeaderboardEntry;
  winCondition: LeaderboardWinCondition | undefined;
  totalParticipants: number;
};

type LeaderboardItem = {
  wallet_address: string;
  x_username?: string;
  x_user_id?: string;
  balance: number;
  rank: number;
};

type LeaderboardResponse = {
  top_wallets_point_balance: LeaderboardItem[];
  user_balance: number;
  user_rank: number;
  total_participants: number;
  winning_rank_threshold: number;
  winning_point_threshold: number;
};

export const LeaderboardContext = createContext<{
  leaderboard: LeaderboardData | null;
  isLoading: boolean;
  invalidate: (() => void) | undefined;
}>({
  leaderboard: null,
  isLoading: true,
  invalidate: undefined,
});

const useLeaderboard = () => {
  const ctx = useContext(LeaderboardContext);
  if (!ctx) {
    // Because QuestsProvider requires this for PaaS but not for Onboarding quests return
    // a default value instead of throwing an error.
    return {
      leaderboard: null,
      isLoading: false,
      invalidate: undefined,
      totalParticipants: 0,
    };
  }

  return ctx;
};

type LeaderboardProviderProps = PropsWithChildren<{
  campaignId: number;
  isCreatorLeaderboard?: boolean;
}>;

const LeaderboardProvider = ({
  children,
  campaignId,
  isCreatorLeaderboard = false,
}: LeaderboardProviderProps) => {
  const { walletAddress } = usePassportProvider();
  const getAccessToken = useAccessToken();

  // Create query parameter string if isCreatorLeaderboard is true
  const queryParams = isCreatorLeaderboard
    ? "?is_creator_leaderboard=true"
    : "";

  const {
    data: leaderboardResponse,
    isLoading,
    isValidating,
    mutate,
  } = useSWR<LeaderboardResponse | null>(
    `leaderboard-${campaignId}${isCreatorLeaderboard ? "-creator" : ""}`,
    async () => {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        const resp = await axios.get<LeaderboardResponse>(
          `${appConfig.IMMUTABLE_BASE_URI}/v1/quests/campaign/${campaignId}/public-leaderboard${queryParams}`,
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        );
        if (resp.status === 200) {
          return resp.data;
        }
        return null;
      }
      const response = await authorizedGet<LeaderboardResponse>(
        `/v1/quests/campaign/${campaignId}/leaderboard${queryParams}`,
        accessToken,
      );
      if (response) {
        return response;
      }
      return null;
    },
    {
      revalidateOnFocus: true,
    },
  );

  // Fetch authorised leaderboard when user logs in
  useEffect(() => {
    if (walletAddress) {
      mutate();
    }
  }, [walletAddress, mutate]);

  return (
    <LeaderboardContext.Provider
      value={{
        leaderboard: leaderboardResponse
          ? {
              topEntries:
                leaderboardResponse.top_wallets_point_balance?.map((item) => ({
                  walletAddress: item.wallet_address,
                  xUsername: item.x_username,
                  xUserId: item.x_user_id,
                  balance: item.balance,
                  rank: item.rank,
                })) ?? [],
              // user_rank will be 0 when unauthenticated
              userEntry:
                walletAddress && leaderboardResponse.user_rank !== 0
                  ? {
                      walletAddress: walletAddress,
                      balance: leaderboardResponse.user_balance,
                      rank: leaderboardResponse.user_rank,
                    }
                  : undefined,
              winCondition:
                leaderboardResponse.user_rank >
                  leaderboardResponse.winning_rank_threshold &&
                leaderboardResponse.user_rank !== 0
                  ? {
                      pointsFromWinning:
                        leaderboardResponse.winning_point_threshold -
                        leaderboardResponse.user_balance +
                        1,
                      thresholdRank: leaderboardResponse.winning_rank_threshold,
                    }
                  : undefined,
              totalParticipants: leaderboardResponse.total_participants,
            }
          : null,
        isLoading,
        invalidate: mutate,
      }}
    >
      {children}
    </LeaderboardContext.Provider>
  );
};

export { LeaderboardProvider, useLeaderboard };
